import { IconArrowRight, IconBooks, IconCalendarEvent, IconClockHour4, IconMapPin, IconPhone, IconSpeakerphone, IconUser, IconUsersGroup } from '@tabler/icons-react';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';
import { PosterLogo, PosterSheet, fs, hexToRgba, numberScale, posterFields, valueFitScale } from '../posterParts';
import { toBnDigits } from '../../../../utils/bnFormat';

/**
 * "Timeline" — a roadmap: a slanted title block on top, then five steps on a dashed vertical line (apply from → vacancies →
 * qualification → age → deadline), each in its own card beside a coloured node, a call-to-action and a slanted footer.
 * Palette: bg, top, topDeep, ink, muted, line, nodes (5 colours), hot.
 */
const FONT = "'Noto Sans Bengali', 'Anek Bangla', sans-serif";
const NODE = 10;

function Step({ p, index, color, Icon, label, hot = false, children }) {
    return (
        <div className="relative flex items-center" style={{ gap: fs(3.2), zIndex: 1 }}>
            <div className="flex flex-none items-center justify-center rounded-full" style={{ width: fs(NODE), height: fs(NODE), background: color, boxShadow: `0 0 0 ${fs(1.1)} ${p.bg}, 0 0.8cqw 1.6cqw ${hexToRgba(color, 0.5)}` }}>
                <Icon style={{ width: fs(5.4), height: fs(5.4), color: '#fff' }} stroke={2.2} />
            </div>
            <div className="relative min-w-0 flex-1 overflow-hidden" style={{ background: hot ? hexToRgba(p.hot, 0.1) : '#fff', border: hot ? `0.3cqw solid ${hexToRgba(p.hot, 0.55)}` : '0.2cqw solid rgba(15,23,42,0.06)', borderRadius: fs(2.6), padding: `${fs(1.9)} ${fs(3)}`, boxShadow: '0 0.7cqw 1.7cqw rgba(15,23,42,0.1)' }}>
                <div style={{ color: hot ? p.hot : p.muted, fontSize: fs(2.6), fontWeight: 800, lineHeight: 1.2 }}>{label}</div>
                {children}
                <div className="absolute" style={{ right: fs(2.6), top: '50%', transform: 'translateY(-50%)', color: hexToRgba(color, 0.16), fontSize: fs(9), fontWeight: 900, lineHeight: 1 }}>{toBnDigits(`0${index}`)}</div>
            </div>
        </div>
    );
}

export default function TimelineDesign({ job, business, theme, showFooter = true, className = '' }) {
    const { t } = useTranslation();
    const p = theme.palette;
    const f = posterFields(job, business, t);
    const eduScale = valueFitScale(f.edu, 40);
    const ageScale = valueFitScale(f.age, 30);
    const [c1, c2, c3, c4] = p.nodes;

    const body = (
        <div className="flex flex-1 flex-col">
            {/* slanted title block */}
            <div className="relative" style={{ background: `linear-gradient(135deg, ${p.top} 0%, ${p.topDeep} 100%)`, padding: `${fs(5.4)} 6.5cqw ${fs(10.5)}` }}>
                <div className="flex items-center" style={{ gap: fs(3.4) }}>
                    <PosterLogo job={job} size={17} style={{ background: '#fff', borderRadius: fs(3.6), padding: fs(1.6), boxShadow: '0 0.9cqw 2cqw rgba(0,0,0,0.28)' }} />
                    <div className="min-w-0 flex-1">
                        <div className="inline-flex items-center" style={{ background: 'rgba(255,255,255,0.18)', border: '0.25cqw solid rgba(255,255,255,0.55)', color: '#fff', borderRadius: '99px', padding: `${fs(0.7)} ${fs(2.2)}`, gap: fs(1.1), fontSize: fs(2.8), fontWeight: 800 }}>
                            <IconSpeakerphone style={{ width: fs(3.2), height: fs(3.2) }} stroke={2.2} />
                            <div>{f.label.notice}</div>
                        </div>
                        <div style={{ color: '#fff', fontSize: fs(6.3), fontWeight: 800, lineHeight: 1.14, marginTop: fs(1.6) }}>{f.org}</div>
                        {f.position && <div style={{ color: '#fff', opacity: 0.9, fontSize: fs(3.5), fontWeight: 600, lineHeight: 1.25, marginTop: fs(1) }}>{f.position}</div>}
                    </div>
                </div>
                <svg viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true" style={{ position: 'absolute', left: 0, bottom: '-0.2cqw', width: '100%', height: '7cqw' }}>
                    <polygon points="0,10 100,0 100,10" fill={p.bg} />
                </svg>
            </div>

            {/* the steps */}
            <div className="flex flex-1 flex-col justify-center" style={{ padding: `${fs(0.5)} 6.5cqw ${fs(2.4)}`, gap: fs(2.4) }}>
                <div className="relative flex flex-col" style={{ gap: fs(2.2) }}>
                    <div className="pointer-events-none absolute" style={{ left: `calc(${fs(NODE)} / 2 - 0.3cqw)`, top: fs(NODE / 2), bottom: fs(NODE / 2), borderLeft: `0.6cqw dashed ${hexToRgba(p.line, 0.5)}`, zIndex: 0 }} />

                    <Step p={p} index={1} color={c1} Icon={IconCalendarEvent} label={f.label.start}>
                        <div style={{ color: p.ink, fontSize: fs(4.6), fontWeight: 800, lineHeight: 1.2 }}>{f.start}</div>
                    </Step>
                    <Step p={p} index={2} color={c2} Icon={IconUsersGroup} label={f.label.vacancy}>
                        <div className="flex items-baseline" style={{ gap: fs(1.4) }}>
                            <div style={{ color: c2, fontSize: `calc(${9 * numberScale(f.vacancy)}cqw * var(--fs, 1))`, fontWeight: 900, lineHeight: 1.05 }}>{f.vacancy}</div>
                            <div style={{ color: p.ink, fontSize: fs(4), fontWeight: 800 }}>{f.label.unit}</div>
                        </div>
                    </Step>
                    <Step p={p} index={3} color={c3} Icon={IconBooks} label={f.label.edu}>
                        <div style={{ color: p.ink, fontSize: `calc(${4 * eduScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.25 }}>{f.edu}</div>
                    </Step>
                    <Step p={p} index={4} color={c4} Icon={IconUser} label={f.label.age}>
                        <div style={{ color: p.ink, fontSize: `calc(${4 * ageScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.25 }}>{f.age}</div>
                    </Step>
                    <Step p={p} index={5} color={p.hot} Icon={IconClockHour4} label={f.label.end} hot>
                        <div style={{ color: p.hot, fontSize: fs(4.8), fontWeight: 900, lineHeight: 1.2 }}>{f.end}</div>
                    </Step>
                </div>

                <div style={{ color: p.muted, fontSize: fs(2.6), fontWeight: 600, textAlign: 'center', marginTop: fs(0.6) }}>✓ {f.label.promo}</div>

                <div className="flex items-center justify-between" style={{ background: `linear-gradient(90deg, ${p.top}, ${p.topDeep})`, borderRadius: fs(3), padding: `${fs(2)} ${fs(3.4)}`, color: '#fff', boxShadow: '0 0.9cqw 2cqw rgba(15,23,42,0.22)' }}>
                    <div style={{ fontSize: fs(4.4), fontWeight: 900 }}>{f.label.cta}</div>
                    <IconArrowRight className="flex-none" style={{ width: fs(5.6), height: fs(5.6) }} stroke={3} />
                </div>
            </div>
        </div>
    );

    const footer = showFooter ? (
        <div className="relative text-center" style={{ background: p.topDeep, color: '#fff', padding: `${fs(8)} 6.5cqw ${fs(3.4)}`, marginTop: fs(2) }}>
            <svg viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true" style={{ position: 'absolute', left: 0, top: '-0.2cqw', width: '100%', height: '6cqw' }}>
                <polygon points="0,0 100,0 0,10" fill={p.bg} />
            </svg>
            <div className="flex items-center justify-center" style={{ gap: fs(2.2) }}>
                {f.shop.logo && <img src={f.shop.logo} alt="" className="flex-none rounded-full object-contain" style={{ width: fs(8.4), height: fs(8.4), background: '#fff', padding: '0.6cqw' }} />}
                <div style={{ fontSize: fs(5.8), fontWeight: 800, lineHeight: 1.14, opacity: f.shop.placeholder ? 0.65 : 1 }}>{f.shop.nameShown}</div>
            </div>
            <div className="flex items-center justify-center" style={{ gap: fs(1.2), marginTop: fs(0.8), fontSize: fs(3.6), lineHeight: 1.25, opacity: f.shop.addressPlaceholder ? 0.6 : 0.92 }}>
                <IconMapPin className="flex-none" style={{ width: fs(3.8), height: fs(3.8) }} />
                <div>{f.shop.addressShown}</div>
            </div>
            {f.shop.phone && (
                <div className="flex items-center justify-center" style={{ gap: fs(1.2), marginTop: fs(0.6), fontSize: fs(4.6), fontWeight: 800 }}>
                    <IconPhone className="flex-none" style={{ width: fs(4.2), height: fs(4.2) }} />
                    <div>{f.shop.phone}</div>
                </div>
            )}
        </div>
    ) : null;

    return (
        <PosterSheet
            deps={[job, business, theme, showFooter, t]}
            className={className}
            fontFamily={FONT}
            background={p.bg}
            body={body}
            footer={footer}
        />
    );
}
